import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  private readonly encoder = new TextEncoder();
  private readonly decoder = new TextDecoder();

  obfuscateEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    
    if (localPart.length <= 2) return email;
    
    const charsToObfuscate = Math.floor(localPart.length / 2);
    const startPos = Math.floor((localPart.length - charsToObfuscate) / 2);
    const endPos = startPos + charsToObfuscate;
    
    const obfuscatedPart = localPart.split('')
      .map((char, index) => {
        if (index >= startPos && index < endPos) {
          return 'x';
        }
        return char;
      })
      .join('');
    
    return `${obfuscatedPart}@${domain}`;
  }

  async encryptUrlParams(quotation: number, role: number): Promise<string> {
    const data = `${quotation}|${role}`;
    const encrypted = await this.encrypt(data);
    return encodeURIComponent(encrypted);
  }

  async decryptUrlParams(hash: string): Promise<{ quotation: number; role: number } | null> {
    try {
      const decoded = decodeURIComponent(hash);
      const decrypted = await this.decrypt(decoded);
      const [quotation, role] = decrypted.split('|');
      return {
        quotation: parseInt(quotation),
        role: parseInt(role)
      };
    } catch (error) {
      console.error('Error decrypting URL params:', error);
      return null;
    }
  }

  private async encrypt(text: string): Promise<string> {
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      this.encoder.encode(text)
    );

    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    return btoa(String.fromCharCode(...combined));
  }

  private async decrypt(encryptedText: string): Promise<string> {
    const key = await this.getKey();
    const combined = Uint8Array.from(atob(encryptedText), c => c.charCodeAt(0));
    
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );

    return this.decoder.decode(decrypted);
  }

  private async getKey(): Promise<CryptoKey> {
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(environment.KEY),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: this.encoder.encode('fixed-salt'),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

}